import { postJSON } from "../api.js";
import { esc } from "../ui/format.js";
import { uiCard, uiEmpty, uiGet, uiHeader, uiMoney, uiPct, uiPill, uiPlanDrawer } from "../ui/kit.js";
import { toast } from "../ui/modals.js";
import { openDealPostmortemModal } from "./core/deals.js";

/**
 * Deals & Partners — redesigned (replaces app.js renderDeals).
 *
 * A real sales pipeline: deals as cards moving Discussion → Negotiation →
 * Legal Review → Signed, what each is actually worth (pilots and contracts
 * book revenue; partnerships lift growth), your trust with each company,
 * contract obligations, customer concentration, and who to meet next with
 * an intelligence brief. Data: /api/deals/desk and the existing deal actions.
 */

function dlValue(d) {
  if (d.value_kind === "growth_lift") return `<span class="dl-value">Growth lift <em>· no direct revenue</em></span>`;
  if (d.booked_arr) return `<span class="dl-value">${uiMoney(d.booked_arr, { compact: true })}/yr <em>· booked</em></span>`;
  return `<span class="dl-value">${uiMoney(d.arr_low, { compact: true })}–${uiMoney(d.arr_high, { compact: true })}/yr <em>· estimated</em></span>`;
}

function dlTrust(t) {
  const tone = t >= 65 ? "var(--sage)" : t >= 40 ? "var(--neon-gold)" : "var(--oxblood)";
  return `<div class="dl-trust" title="Trust ${Math.round(t)}/100"><span>Trust</span><div><i style="width:${t}%; background:${tone}"></i></div><b>${Math.round(t)}</b></div>`;
}

function dlCard(d) {
  const open = d.stage !== "Signed" && d.stage !== "Lost";
  return `
    <article class="dl-card" data-deal="${d.id}">
      <div class="dl-card-top"><strong>${esc(d.company)}</strong>${uiPill(d.type, d.type === "Contract" ? "good" : d.type === "Pilot" ? "info" : "")}</div>
      ${dlValue(d)}
      ${open && d.probability !== null ? `<div class="dl-prob"><span>Chance it closes as-is</span><div><i style="width:${Math.round(d.probability * 100)}%"></i></div><b>${Math.round(d.probability * 100)}%</b></div>` : ""}
      ${dlTrust(d.trust)}
      <div class="dl-meta">Met M${d.met_month}${d.negotiations ? ` · ${d.negotiations} round${d.negotiations > 1 ? "s" : ""} of negotiation` : ""}</div>
      ${open ? (d.ready ? `
        <div class="dl-actions">
          <button type="button" class="primary" data-act="sign" data-id="${d.id}">Sign</button>
          <button type="button" data-act="aggressive" data-id="${d.id}" title="Better terms if it works; they may walk">Push hard</button>
          <button type="button" data-act="collaborative" data-id="${d.id}" title="Small improvement, low risk">Negotiate</button>
          <button type="button" data-act="reject" data-id="${d.id}">Walk away</button>
        </div>` : `<div class="dl-wait">Their response arrives next month.</div>`)
      : `<button type="button" class="dl-why" data-why="${d.id}">Why did this happen?</button>`}
    </article>`;
}

/** Signed stays short: the total, the newest deal, and a button to see them all (a long column made the page endless). */
function dlSignedCol(list) {
  if (!list.length) return `<div class="dl-empty">—</div>`;
  const booked = list.reduce((a, d) => a + (d.booked_arr || 0), 0);
  const newest = list.slice().sort((a, b) => (b.signed_month || b.met_month || 0) - (a.signed_month || a.met_month || 0))[0];
  return `<div class="dl-signed-sum"><b>${list.length}</b> signed<span>${booked ? `${uiMoney(booked, { compact: true })}/yr booked` : "pilots and partnerships"}</span></div>
    ${dlCard(newest)}
    ${list.length > 1 ? `<button type="button" class="primary dl-signed-all" data-signed-all>See all ${list.length} signed deals</button>` : ""}`;
}

function dlOpenSigned(list, all) {
  const back = document.createElement("div");
  back.className = "modal-backdrop";
  back.innerHTML = `<div class="modal dl-signed-modal" role="dialog" aria-label="Signed deals">
      <h3>Signed deals (${list.length})</h3>
      <p class="bk-sub">Every pilot, contract and partnership you've signed. Tap one for its details.</p>
      <div class="dl-signed-grid">${list.map(dlCard).join("")}</div>
      <div class="modal-actions"><button type="button" class="primary" data-close>Close</button></div></div>`;
  document.body.appendChild(back);
  const close = () => back.remove();
  back.querySelector("[data-close]").addEventListener("click", close);
  back.addEventListener("click", (e) => { if (e.target === back) close(); });
  back.querySelectorAll("[data-why]").forEach((b) => b.addEventListener("click", (e) => { e.stopPropagation(); close(); openDealPostmortemModal("/deals", parseInt(b.dataset.why, 10)); }));
  back.querySelectorAll(".dl-card").forEach((c) => c.addEventListener("click", () => { close(); dlDetail(all.find((d) => d.id === parseInt(c.dataset.deal, 10))); }));
}

function dlDetail(d) {
  uiPlanDrawer({
    eyebrow: `${d.type} · ${d.stage}`, title: d.company, description: d.summary,
    effect: d.value_kind === "growth_lift" ? "A partnership books no revenue itself: it lifts your growth for about a year."
      : "Signed pilots and contracts become a revenue line with an SLA and a renewal date.",
    risk: d.terms && d.terms.length ? `Terms: ${d.terms.join(" · ")}` : "",
    input: null, costFor: () => null, forecast: null, commitLabel: "Close",
    commit: async () => null,
  });
}

let dlSector = "All";

function dlSlides(companies) {
  const list = dlSector === "All" ? companies : companies.filter((c) => c.sector === dlSector);
  return list.map((c) => `
    <button type="button" class="dl-slide ${c.met ? "met" : ""}" data-meet-plan="${esc(c.name)}">
      <div class="dl-slide-top"><span class="dl-pick-rank">#${c.world_rank}</span>${c.fit === "Natural partner" ? uiPill("Natural partner", "good") : c.fit === "Good fit" ? uiPill("Good fit", "info") : ""}</div>
      <b>${esc(c.name)}</b>
      <em>${esc(c.sector)} · ${esc(c.country)}</em>
      <span class="dl-slide-rev">${uiMoney(c.annual_revenue, { compact: true })} revenue</span>
      <span class="dl-slide-focus">${esc(c.focus)}</span>
      <span class="dl-pick-go">${c.met ? "Met before · meet again →" : "Read the brief →"}</span>
    </button>`).join("") || `<div class="dl-note">No companies in this sector.</div>`;
}

// ---------------------------------------------------------------- sub-tabs (2.7): Pipeline / Contracts / Tenders
// The page was about three times longer than any other; each part now has its own tab, remembered in this browser.
const DL_PANES = [["pipeline", "🤝 Pipeline"], ["contracts", "🔁 Contracts"], ["tenders", "📑 Tenders"]];

function dlPane() {
  try { const v = localStorage.getItem("ges.deals.tab"); return DL_PANES.some(([k]) => k === v) ? v : "pipeline"; } catch (e) { return "pipeline"; }
}

function dlSubTabs(desk, ct) {
  const open = desk.deals.filter((d) => !["Signed", "Lost"].includes(d.stage)).length;
  const renewals = ct ? ct.letters.filter((d) => d.kind === "renewal").length : 0;
  const n = { pipeline: open, contracts: ct ? ct.renewals.length : desk.obligations.length, tenders: ct ? ct.tenders.length : 0 };
  const due = { pipeline: false, contracts: renewals > 0, tenders: !!(ct && (ct.tenders.length || ct.letters.some((d) => d.kind !== "renewal"))) };
  return `<div class="dl-subtabs" role="tablist" aria-label="Deals sections">${DL_PANES.map(([k, l]) => `
    <button type="button" role="tab" class="dl-subtab" data-dl-tab="${k}">${l}<span class="dl-subtab-n${due[k] ? " due" : ""}">${n[k]}</span></button>`).join("")}</div>`;
}

function dlShowPane(main, key) {
  try { localStorage.setItem("ges.deals.tab", key); } catch (e) { /* not remembered */ }
  main.querySelectorAll("[data-dl-pane]").forEach((p) => { p.hidden = p.dataset.dlPane !== key; });
  main.querySelectorAll("[data-dl-tab]").forEach((b) => { const on = b.dataset.dlTab === key; b.classList.toggle("on", on); b.setAttribute("aria-selected", String(on)); });
}

window.renderDeals = async function (main, snap) {
  const [desk, dir, ct] = await Promise.all([uiGet("/deals/desk"), uiGet("/companies/search?top_only=false&limit=200"),
    uiGet("/contracts").catch(() => null)]);
  const companies = dir.results;
  const sectors = dir.sectors.filter((sec) => companies.some((c) => c.sector === sec));
  const k = desk.kpis;
  const byStage = Object.fromEntries(desk.stages.map((s) => [s, desk.deals.filter((d) => d.stage === s)]));
  main.innerHTML = `
    <div class="sx">
      ${uiHeader(`Deals & Partners — Month ${snap.month}`, "Business development the way it really works: take a meeting, their terms arrive a month later, then sign, negotiate or walk away. Trust you build — or burn — follows you to the next deal.")}
      ${dlSubTabs(desk, ct)}
      <div data-dl-pane="pipeline">
      <section class="dl-hero dl-hero-slider">
        <div class="dl-hero-head">
          <div><div class="sx-eyebrow">Your next move</div><h3>Take a business meeting</h3>
            <p>Slide through the world's largest companies, best fits for you first. Tap one to read the brief and meet them.</p></div>
          <div class="dl-slide-nav"><button type="button" class="dl-arrow" data-slide="-1" aria-label="Previous companies">◀</button>
            <button type="button" class="dl-arrow" data-slide="1" aria-label="Next companies">▶</button></div>
        </div>
        <p class="dl-one-talk">Every meeting is one conversation with the company: read their brief, walk into the room and talk. Their terms arrive next month.</p>
        <div class="dl-chips" id="dl-chips">${["All", ...sectors].map((sec) => `<button type="button" class="dl-chip ${sec === dlSector ? "on" : ""}" data-chip="${esc(sec)}">${esc(sec)}</button>`).join("")}</div>
        <div class="dl-track" id="dl-track">${dlSlides(companies)}</div>
        <div class="dl-hero-go"><button type="button" class="primary" id="dl-plan">Request a meeting</button></div>
      </section>
      ${k.open || byStage.Signed.length ? `<div class="dl-board">
        ${["Discussion", "Negotiation", "Legal Review", "Signed"].map((s) => `
          <section class="dl-col">
            <div class="dl-col-head"><span>${esc(s)}</span><b>${byStage[s].length}</b></div>
            ${s === "Signed" ? dlSignedCol(byStage.Signed)
              : byStage[s].map(dlCard).join("") || `<div class="dl-empty">${s === "Discussion" ? "Take a meeting to start a deal." : "—"}</div>`}
          </section>`).join("")}
      </div>` : ""}
      ${byStage.Lost.length ? `<details class="sx-card"><summary>Walked away or lost (${byStage.Lost.length})</summary>
        <div class="dl-lost">${byStage.Lost.map(dlCard).join("")}</div></details>` : ""}
      ${uiCard("Relationships", desk.relationships.length ? `<table class="sx-table">
        <tr><th>Company</th><th>Trust</th><th class="num">Signed</th><th class="num">Walked</th><th class="num">Last contact</th></tr>
        ${desk.relationships.map((r) => `<tr><td>${esc(r.name)}</td><td style="min-width:120px;">${dlTrust(r.trust_score)}</td>
          <td class="num">${r.deals_signed}</td><td class="num">${r.deals_rejected}${r.deals_backfired ? ` <span class="neg">(${r.deals_backfired} backfired)</span>` : ""}</td>
          <td class="num">M${r.last_interaction_month}</td></tr>`).join("")}</table>`
        : uiEmpty("No relationships yet", "Every meeting, signature and walk-away is remembered."), { sub: "They remember how you treated them" })}
      </div>
      <div data-dl-pane="contracts">
      <div id="dl-contracts"></div>
      ${desk.obligations.length ? uiCard("Contract obligations", `<table class="sx-table">
        <tr><th>Customer</th><th>Service level</th><th class="num">SLA breaches</th><th class="num">Renews</th><th class="num">Status</th></tr>
        ${desk.obligations.map((o) => `<tr><td>${esc(o.company_name)}</td><td>${esc(o.sla_tier)}</td>
          <td class="num ${o.sla_breach_count ? "neg" : ""}">${o.sla_breach_count || 0}</td><td class="num">M${o.renewal_month}</td>
          <td class="num">${esc(o.status || "active")}</td></tr>`).join("")}</table>`,
        { sub: "Signed pilots and contracts come with a service level you must keep, and a renewal you must win" }) : ""}
      </div>
      <div data-dl-pane="tenders"><div id="dl-tenders"></div></div>
    </div>`;
  if (ct && window.dealsContractsPane) window.dealsContractsPane(main.querySelector("#dl-contracts"), ct);
  if (ct && window.dealsTendersPane) window.dealsTendersPane(main.querySelector("#dl-tenders"), ct);
  dlShowPane(main, dlPane());
  main.querySelectorAll("[data-dl-tab]").forEach((b) => b.addEventListener("click", () => dlShowPane(main, b.dataset.dlTab)));

  // one way into a meeting: the brief, then the live conversation (meetings.js -> conversation.js)
  const plan = (name) => window.openMeetCompanyModal(name);
  main.querySelector("#dl-plan").addEventListener("click", () => window.openMeetCompanyModal());
  const wireSlides = () => main.querySelectorAll("#dl-track [data-meet-plan]").forEach((b) => b.addEventListener("click", () => plan(b.dataset.meetPlan)));
  wireSlides();
  const track = main.querySelector("#dl-track");
  main.querySelectorAll("[data-slide]").forEach((b) => b.addEventListener("click", () => {
    track.scrollBy({ left: Number(b.dataset.slide) * track.clientWidth * 0.85, behavior: "smooth" });
  }));
  main.querySelectorAll("[data-chip]").forEach((b) => b.addEventListener("click", () => {
    dlSector = b.dataset.chip;
    main.querySelectorAll("[data-chip]").forEach((x) => x.classList.toggle("on", x === b));
    track.innerHTML = dlSlides(companies);
    track.scrollLeft = 0;
    wireSlides();
  }));
  main.querySelectorAll("[data-act]").forEach((b) => b.addEventListener("click", async (e) => {
    e.stopPropagation();
    const id = parseInt(b.dataset.id, 10), act = b.dataset.act;
    if (act === "reject" && !confirm("Walk away from this deal? They'll remember it.")) return;
    const r = act === "sign" ? await postJSON("/deals/sign", { deal_id: id })
      : act === "reject" ? await postJSON("/deals/reject", { deal_id: id })
      : await postJSON("/deals/negotiate", { deal_id: id, tactic: act });
    toast(r.message); await window.refreshAll();
  }));
  main.querySelectorAll("[data-why]").forEach((b) => b.addEventListener("click", (e) => { e.stopPropagation(); openDealPostmortemModal("/deals", parseInt(b.dataset.why, 10)); }));
  main.querySelectorAll(".dl-card").forEach((c) => c.addEventListener("click", () => dlDetail(desk.deals.find((d) => d.id === parseInt(c.dataset.deal, 10)))));
  main.querySelector("[data-signed-all]")?.addEventListener("click", () => dlOpenSigned(byStage.Signed, desk.deals));
};
